package com.writerslab

import androidx.compose.runtime.Composable
import androidx.compose.runtime.CompositionLocalProvider
import androidx.compose.runtime.compositionLocalOf
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.setValue
import kotlinx.browser.document
import kotlinx.browser.localStorage
import kotlinx.browser.window


private val Translations = mapOf(
    "en" to EnStrings,
    "ru" to RuStrings,
)

val SUPPORTED_LOCALES = Translations.keys.sorted()

// Deliberately initialised with a static default — locale auto-detection
// happens later via [initLocale]. Doing it here as a top-level expression
// races with Translations / SUPPORTED_LOCALES init order in the Kotlin/JS
// development bundle and can throw before the app even mounts.
private var appLanguageTag: String by mutableStateOf("en")

val LocalStrings = compositionLocalOf<Strings> {
    error("Strings not provided. Wrap content in ProvideAppStrings.")
}

/** Global nudge callback — shows a brief bottom message. Provided at the root. */
val LocalNudge = compositionLocalOf<(String) -> Unit> { {} }

@Composable
fun ProvideAppStrings(content: @Composable () -> Unit) {
    CompositionLocalProvider(LocalStrings provides Translations.getValue(appLanguageTag), content = content)
}

/**
 * Pick a locale from localStorage or browser language. Call once during
 * `main()` before rendering Compose — keeps the init off of top-level
 * property delegates where init order is fragile in dev builds.
 */
fun initLocale() {
    val saved = localStorage.getItem("lang")?.takeIf { it in Translations }
    val auto = pickFromBrowserLocale()
    appLanguageTag = saved ?: auto
    syncDocumentLocale()
}

fun setAppLocale(locale: String) {
    if (locale !in Translations || locale == appLanguageTag) return
    appLanguageTag = locale
    localStorage.setItem("lang", locale)
    syncDocumentLocale()
}

/** Read-only accessor for analytics / logging. Avoid using this for
 *  rendering — Compose call sites must read [LocalStrings] so they
 *  recompose when the locale toggles. */
fun currentLocaleTag(): String = appLanguageTag

/**
 * Mirror the active locale onto `<html lang>` and rewrite the
 * locale-sensitive meta tags (title, description, og:*, twitter:*).
 * Note: social-network link previewers (Telegram, Twitter) fetch the
 * page server-side and never execute JS, so they always see whichever
 * language is hard-coded in `index.html`. This function takes care of
 * the in-browser tab title plus crawlers that DO run JS (Google).
 */
private fun syncDocumentLocale() {
    val s = Translations.getValue(appLanguageTag)
    document.documentElement?.setAttribute("lang", appLanguageTag)
    document.title = s.metaTitle
    setMetaContent("name", "description", s.metaDescription)
    setMetaContent("property", "og:title", s.metaTitle)
    setMetaContent("property", "og:description", s.metaDescription)
    setMetaContent("property", "og:locale", s.metaLocale)
    setMetaContent("name", "twitter:title", s.metaTitle)
    setMetaContent("name", "twitter:description", s.metaDescription)
}

private fun setMetaContent(attr: String, key: String, value: String) {
    val sel = "meta[$attr=\"$key\"]"
    document.querySelector(sel)?.setAttribute("content", value)
}

@Composable
fun currentAppLocale(): String = appLanguageTag

/** Non-Composable accessor for callers outside the Compose tree (e.g. HTTP repos). */
fun appLocaleSnapshot(): String = appLanguageTag


private fun pickFromBrowserLocale(): String {
    val nav = window.navigator.language.lowercase()
    for (locale in SUPPORTED_LOCALES) {
        // Manual prefix-match — avoids a Kotlin/JS dev-build bug where
        // String.startsWith(CharSequence) tries `a.a()` on the prefix and
        // throws TypeError before the app can mount.
        if (nav.length >= locale.length && nav.substring(0, locale.length) == locale) {
            return locale
        }
    }
    return "en"
}
